// Фоновые загрузки ролика редактора — близнец очереди картинок
// (`UploadQueueContext`). Провайдер стоит над панелью блока, поэтому загрузка
// переживает уход автора в другой блок или урок; без провайдера (тесты, места
// вне редактора) — прежнее поведение: загрузка живёт и умирает с компонентом.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useStore } from 'react-redux'
import { toast } from 'sonner'
import type { RootState } from '@/store'
import { useCreateVideoMutation } from '@/features/editor/videosApi'
import { uploadKinescopeVideo } from '@/features/editor/lib/uploadKinescopeVideo'
import { resolveAlias } from '@/features/editor/lib/idAliases'
import { uploadErrorText } from '@/api/uploadError'
import { placeKey } from './uploadTask'
import { useBlockUploadCommit } from './useBlockUploadCommit'
import {
  createVideoUploadStore,
  type VideoUploadDeps,
  type VideoUploadStore,
  type VideoUploadTarget,
} from './videoUploadStore'

const VideoUploadContext = createContext<VideoUploadStore | null>(null)

/** Хранилище с зависимостями ПОСЛЕДНЕГО рендера: задача читает их в момент коммита. */
const useStoreWithDeps = (
  canon: VideoUploadDeps['canon'],
  canonLesson: VideoUploadDeps['canonLesson'],
  commitBackground: VideoUploadDeps['commitBackground'],
): VideoUploadStore => {
  const [createVideo] = useCreateVideoMutation()
  const deps: VideoUploadDeps = {
    upload: (file, title, onProgress, signal) =>
      uploadKinescopeVideo({
        file,
        title,
        createVideo: (arg) => createVideo(arg).unwrap(),
        onProgress,
        signal,
      }),
    canon,
    canonLesson,
    commitBackground,
    onError: (e) => toast.error(uploadErrorText(e, 'video')),
  }
  const depsRef = useRef(deps)
  useEffect(() => {
    depsRef.current = deps
  })
  const [store] = useState(() => createVideoUploadStore(() => depsRef.current))
  return store
}

const keyOnly = (target: VideoUploadTarget) => target.key
const dropBackground = () => undefined

interface Props {
  children: ReactNode
  courseId: number
}

export const VideoUploadProvider = ({ children, courseId }: Props) => {
  const reduxStore = useStore<RootState>()
  // Тот же коммит, что у фоновой картинки: свежее тело блока, алиасы id,
  // гейты прогона/проверки/грязного черновика и ожидание оседания источника.
  const buildCommit = useBlockUploadCommit(courseId, 'video')
  const canon = (target: VideoUploadTarget) => {
    if (!target.block) return target.key
    const aliases = reduxStore.getState().editor.idAliases
    return placeKey({
      ...target.block,
      blockId: resolveAlias(aliases, 'block', target.block.blockId),
    })
  }
  const canonLesson = (lessonId: number) =>
    resolveAlias(reduxStore.getState().editor.idAliases, 'lesson', lessonId)
  const store = useStoreWithDeps(canon, canonLesson, (block, videoId) =>
    buildCommit(block)(videoId),
  )
  return <VideoUploadContext.Provider value={store}>{children}</VideoUploadContext.Provider>
}

/** Дефолт без провайдера — сегодняшнее поведение: загрузка принадлежит
 *  компоненту и отменяется на его размонтировании. */
// eslint-disable-next-line react-refresh/only-export-components
export const useVideoUploads = (): VideoUploadStore => {
  const shared = useContext(VideoUploadContext)
  const local = useStoreWithDeps(keyOnly, undefined, dropBackground)
  useEffect(() => () => local.abortAll(), [local])
  return shared ?? local
}
